import { useEffect, useMemo } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import ApiClient from 'c-data/apiClient';
import type { ElasticSearchFilters } from 'c-sdk';
import { EsPCAReportField, EsPCAReportFilterField } from 'c-sdk';
import type { AllEsPCAFieldValues } from 'c-main/Lib';
import { allFieldValuesEs } from 'c-main/Lib';
import { atom_environmentMode } from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import type { AwaitingPostingLine, PostingLine, PostingReportRow } from './lib';
import {
    activePostingFilters,
    AWAITING_FILTER_FIELDS,
    buildPostingLines,
    collectReportRows,
    DEFAULT_POSTING_WINDOW,
    matchesPostingFilters,
    PostingStatus,
    withStatus,
} from './lib';

/**
 * The page's control; the rail badge reads the same value, so changing it moves the badge too.
 */
export const atom_classicPostingWindow = atom<number>(DEFAULT_POSTING_WINDOW);

type PostingsEntry = {
    lines: PostingLine[];
    dataThrough: string | null;
    /** Filter options from the report response, for `ReportFieldFilterES`. */
    filterValues: AllEsPCAFieldValues | null;
    postcodes: Map<string, string>;
    error: string | null;
};

const NO_FILTERS: ElasticSearchFilters = {};

const atom_classicPostings = atom<Record<string, PostingsEntry>>({});

/** Values in the CAD are capitalised; `classic` silently matches nothing. */
const CLASSIC_DISPLAY_TYPE = 'Classic';

const GROUP_BY_FIELDS = [
    EsPCAReportField.DisplayId,
    EsPCAReportField.StartDate,
    EsPCAReportField.FrameId,
    EsPCAReportField.DisplayScreenNameSummary,
    EsPCAReportField.DisplayOwner,
    EsPCAReportField.DisplayTown,
    EsPCAReportField.DisplayTvRegion,
    EsPCAReportField.DisplayFormat,
];

/**
 * Booked classic lines, from the backend's definition of "booked" (the campaign's schedule). Those
 * with no CAD rows at all are awaiting data; the rest are already in the report. Every line's ZIP
 * comes from here too, since the CAD has none.
 *
 * Each line's booked start is resolved on the backend: its own schedules' earliest rule start, then
 * its line item's dates, then the campaign start (`booked_start_source` says which).
 */
const fetchBookedLines = async (
    campaignId: number,
    reportedDisplayIds: Set<string>,
): Promise<{ awaiting: AwaitingPostingLine[]; postcodes: Map<string, string> }> => {
    const response = await ApiClient.Reports.classicBookedLines({ id: campaignId }).request;
    const booked = response?.data?.data ?? [];

    const postcodes = new Map(
        booked.filter(d => d.postcode).map(d => [String(d.display_id), d.postcode] as const),
    );
    const awaiting = booked
        .filter(d => !reportedDisplayIds.has(String(d.display_id)))
        .map(d => ({
            displayId: String(d.display_id),
            frameId: d.frame_id ?? '',
            name: d.name || d.address || '',
            owner: d.owner_name ?? '',
            ownerId: d.owner_id != null ? String(d.owner_id) : undefined,
            market: d.town ?? '',
            state: d.tv_region ?? '',
            format: d.format ?? '',
            postcode: d.postcode ?? undefined,
            bookedStart: d.booked_start,
        }));
    return { awaiting, postcodes };
};

const reportRows = async (
    campaignId: number,
    environment: string,
    fieldFilters: ElasticSearchFilters,
    showFilters: boolean,
) => {
    // No date range: the API only excludes projected rows (inside_campaign_settings_date = 0)
    // when no dates are sent, and those must never reach a delivered figure.
    const report = await ApiClient.ReportsElasticSearch.pcaGroupByField({
        campaignId,
        environment,
        source: 'athena',
        fields: GROUP_BY_FIELDS,
        fieldFilters: {
            ...fieldFilters,
            [EsPCAReportFilterField.DisplayType]: [CLASSIC_DISPLAY_TYPE],
        },
        showFilters,
    }).request;
    return {
        rows: collectReportRows(report?.data?.data) as PostingReportRow[],
        filters: report?.data?.filters,
    };
};

export const CLASSIC_POSTINGS_FILTER_FIELDS = Object.keys(
    AWAITING_FILTER_FIELDS,
) as EsPCAReportFilterField[];

/**
 * The whole campaign, unfiltered. It decides which booked lines never reported -- a filtered
 * report leaves out units that did report, which must not then look like they are awaiting data --
 * and it carries the filter options, so they don't narrow as filters are applied.
 */
const fetchCampaignPostings = async (
    campaignId: number,
    environment: string,
): Promise<PostingsEntry> => {
    const { rows, filters } = await reportRows(campaignId, environment, {}, true);
    const reported = new Set(rows.map(r => String(r.display_id)));

    // A failure here leaves the page showing every line in the report; it just can't see lines that
    // never reported.
    const { awaiting, postcodes } = await fetchBookedLines(campaignId, reported).catch(() => ({
        awaiting: [] as AwaitingPostingLine[],
        postcodes: new Map<string, string>(),
    }));
    const { lines, dataThrough } = buildPostingLines(rows, awaiting);
    return {
        lines: lines.map(l => ({ ...l, postcode: l.postcode ?? postcodes.get(l.displayId) })),
        dataThrough,
        filterValues: filters ? allFieldValuesEs(filters) : null,
        postcodes,
        error: null,
    };
};

const cache = new Map<string, Promise<PostingsEntry>>();

const cached = (key: string, load: () => Promise<PostingsEntry>) => {
    if (!cache.has(key)) {
        cache.set(
            key,
            load().catch((e): PostingsEntry => {
                cache.delete(key); // let a later visit retry
                return {
                    lines: [],
                    dataThrough: null,
                    filterValues: null,
                    postcodes: new Map(),
                    error: String(e?.message ?? e ?? 'Error'),
                };
            }),
        );
    }
    return cache.get(key);
};

const loadPostings = (
    campaignId: number,
    environment: string,
    fieldFilters: ElasticSearchFilters,
): Promise<PostingsEntry> => {
    const campaignKey = `${campaignId}:${environment}`;
    const campaign = cached(campaignKey, () => fetchCampaignPostings(campaignId, environment));
    if (!Object.keys(fieldFilters).length) return campaign;

    return cached(`${campaignKey}:${JSON.stringify(fieldFilters)}`, async () => {
        const [whole, { rows }] = await Promise.all([
            campaign,
            reportRows(campaignId, environment, fieldFilters, false),
        ]);
        if (whole.error) throw new Error(whole.error);
        const awaiting = whole.lines.filter(
            l => l.postedUp == null && !rows.some(r => String(r.display_id) === l.displayId),
        );
        const { lines, dataThrough } = buildPostingLines(
            rows,
            awaiting.filter(l => matchesPostingFilters(l, fieldFilters)),
        );
        return {
            lines: lines.map(l => ({
                ...l,
                postcode: l.postcode ?? whole.postcodes.get(l.displayId),
            })),
            dataThrough,
            filterValues: whole.filterValues,
            postcodes: whole.postcodes,
            error: null,
        };
    });
};

/**
 * `fieldFilters` go to the API, like the other reports. The posting window never does: status is
 * derived here, so changing the window re-derives everything without a request.
 */
const useClassicPostings = (
    campaignId: number,
    enabled = true,
    fieldFilters: ElasticSearchFilters = NO_FILTERS,
) => {
    const environment = useAtomValue(atom_environmentMode) || 'live';
    const filters = useMemo(() => activePostingFilters(fieldFilters), [fieldFilters]);
    const cacheKey = `${campaignId}:${environment}:${JSON.stringify(filters)}`;
    const [entries, setEntries] = useAtom(atom_classicPostings);
    const windowDays = useAtomValue(atom_classicPostingWindow);
    const entry = entries[cacheKey];

    useEffect(() => {
        if (!enabled || !campaignId || entry) return;
        (async () => {
            const result = await loadPostings(campaignId, environment, filters);
            setEntries(prev => (prev[cacheKey] ? prev : { ...prev, [cacheKey]: result }));
        })();
    }, [enabled, campaignId, environment, filters, cacheKey, entry, setEntries]);

    const lines = useMemo(
        () => withStatus(entry?.lines ?? [], windowDays),
        [entry?.lines, windowDays],
    );
    const outOfWindow = useMemo(
        () => lines.filter(l => l.status === PostingStatus.Out).length,
        [lines],
    );

    return {
        lines,
        outOfWindow,
        windowDays,
        filterValues: entry?.filterValues ?? null,
        dataThrough: entry?.dataThrough ?? null,
        error: entry?.error ?? null,
        isLoading: enabled && !entry,
    };
};

export default useClassicPostings;
